import React from 'react'
import { Icon, Radio, Table, Tooltip, Typography } from 'antd'
import { values } from 'lodash-es'
import PropTypes from 'prop-types'

import { HiddenInputs, Input, Select } from '../../../../core/components'
import { Button } from '../../../../core/components/styled'

import { MENU_CATEGORIES } from '../../../../menus/constants'
import { isDocumentFileEligibleForScraping } from '../../../helpers'

const MenuDocumentFilesSection = ({
  menuDocumentFiles,
  onMenuDocumentFileSelect,
  onFileCategoryChange,
  applyInheritedCategoryToConfirmed,
  applyPredictedCategoryToConfirmed,
  applyInheritedTitleToConfirmed,
  applyPredictedTitleToConfirmed,
  onMenuDocumentFileNameClick,
}) => {
  const menuDocumentFilesTableColumns = [
    {
      dataIndex: 'isSelected',
      width: '2%',
      render: (text, record, index) => {
        const isCategoryAllowed = isDocumentFileEligibleForScraping(
          record.fileCategory,
        )
        return (
          <div
            className={isCategoryAllowed ? '' : 'disabled-radio-container'}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '32px',
            }}
          >
            <Radio
              className={isCategoryAllowed ? 'custom-blue-radio' : ''}
              checked={record.isSelected}
              disabled={!isCategoryAllowed}
              onClick={() => onMenuDocumentFileSelect(index)}
            />
          </div>
        )
      },
    },
    {
      title: 'Inherited File Category',
      dataIndex: 'fileInheritedCategory',
      align: 'left',
      width: '7%',
      render: (fileInheritedCategory, record, index) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            width: '100%',
          }}
        >
          {MENU_CATEGORIES[fileInheritedCategory]?.name || '-'}
          <Button
            type="link"
            onClick={() => applyInheritedCategoryToConfirmed(index)}
            disabled={
              !record.fileInheritedCategory ||
              record.fileCategory === record.fileInheritedCategory
            }
            title="Apply inherited to confirmed"
            margin="no no no small"
            style={{ marginLeft: 'auto' }}
          >
            <Icon type="arrow-right" />
          </Button>
        </div>
      ),
    },
    {
      title: 'Predicted File Category',
      dataIndex: 'filePredictionCategory',
      align: 'left',
      width: '7%',
      render: (filePredictionCategory, record, index) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            width: '100%',
          }}
        >
          {MENU_CATEGORIES[filePredictionCategory]?.name || '-'}
          <Button
            type="link"
            onClick={() => applyPredictedCategoryToConfirmed(index)}
            disabled={
              !filePredictionCategory ||
              record.fileCategory === filePredictionCategory
            }
            title="Apply predicted to confirmed"
            margin="no no no small"
            style={{ marginLeft: 'auto' }}
          >
            <Icon type="arrow-right" />
          </Button>
        </div>
      ),
    },
    {
      title: 'Confirmed File Category',
      dataIndex: 'fileCategory',
      width: '7%',
      render: (fileCategory, record, index) => {
        const getConfirmationNote = file => {
          if (!file || !file.fileCategory) return null

          if (file.fileCategory === file.fileInheritedCategory)
            return 'Inherited value'
          if (file.fileCategory === file.filePredictionCategory)
            return 'Predicted value'
          return 'Manually set value'
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Select
              name={`menuDocumentFiles.${index}.fileCategory`}
              options={values(MENU_CATEGORIES)}
              onSelectChange={e => onFileCategoryChange(index, e)}
              showSearch
            />
            {getConfirmationNote(record) && (
              <span
                style={{
                  marginTop: 4,
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'rgba(0,0,0,0.45)',
                }}
              >
                {getConfirmationNote(record)}
              </span>
            )}
          </div>
        )
      },
    },
    {
      title: 'Inherited File Title',
      dataIndex: 'fileInheritedTitle',
      align: 'left',
      width: '10%',
      render: (fileInheritedTitle, record, index) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {fileInheritedTitle || '-'}
          <Button
            type="link"
            onClick={() => applyInheritedTitleToConfirmed(index)}
            disabled={
              !record.isSelected ||
              !record.fileInheritedTitle ||
              record.fileTitle === record.fileInheritedTitle
            }
            title="Apply inherited to confirmed"
            margin="no no no small"
            style={{ marginLeft: 'auto' }}
          >
            <Icon type="arrow-right" />
          </Button>
        </div>
      ),
    },
    {
      title: 'Predicted File Title',
      dataIndex: 'filePredictionTitle',
      align: 'left',
      width: '10%',
      render: (filePredictionTitle, record, index) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {filePredictionTitle}
          <Button
            type="link"
            onClick={() => applyPredictedTitleToConfirmed(index)}
            disabled={
              !record.isSelected ||
              !filePredictionTitle ||
              record.fileTitle === filePredictionTitle
            }
            title="Apply predicted to confirmed"
            margin="no no no small"
            style={{ marginLeft: 'auto' }}
          >
            <Icon type="arrow-right" />
          </Button>
        </div>
      ),
    },
    {
      title: 'Confirmed File Title',
      width: '10%',
      render: (fileTitle, record, index) => {
        const getTitleConfirmationNote = file => {
          if (!file || !file.fileTitle) return null

          if (file.fileTitle === file.fileInheritedTitle)
            return 'Inherited value'
          if (file.fileTitle === file.filePredictionTitle)
            return 'Predicted value'
          return 'Manually set value'
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Input
              name={`menuDocumentFiles.${index}.fileTitle`}
              placeholder="File title"
              disabled={!record.isSelected}
            />
            {getTitleConfirmationNote(record) && (
              <span
                style={{
                  marginTop: 4,
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'rgba(0,0,0,0.45)',
                }}
              >
                {getTitleConfirmationNote(record)}
              </span>
            )}
          </div>
        )
      },
    },
    {
      title: 'File Text',
      dataIndex: 'fileText',
      align: 'left',
      width: '8%',
      render: fileText => `«${fileText}»`,
    },
    {
      title: 'File Name',
      dataIndex: 'fileName',
      align: 'left',
      width: '15%',
      render: (fileName, record) => (
        <Button
          type="link"
          onClick={() => onMenuDocumentFileNameClick(record)}
          style={{
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            textAlign: 'left',
            height: 'auto',
            lineHeight: '1.4',
            padding: '4px 8px',
            maxWidth: '100%',
          }}
        >
          {fileName}
        </Button>
      ),
    },
    {
      title: 'Menu Score',
      dataIndex: 'filePredictionMenuScore',
      align: 'left',
      width: '6%',
      render: filePredictionMenuScore => filePredictionMenuScore || '-',
    },
    {
      title: 'Dish Count',
      dataIndex: 'filePredictionDishCount',
      align: 'left',
      width: '6%',
      render: filePredictionDishCount => filePredictionDishCount || '-',
    },
    {
      title: 'Prediction Rationale',
      dataIndex: 'filePredictionRationale',
      align: 'left',
      width: '25%',
      render: filePredictionRationale => {
        return (
          <Tooltip
            placement="bottom"
            title={filePredictionRationale}
            trigger="hover"
          >
            <Typography.Paragraph
              ellipsis={{ rows: 2 }}
              style={{ marginBottom: 0, cursor: 'pointer' }}
            >
              {filePredictionRationale}{' '}
            </Typography.Paragraph>
          </Tooltip>
        )
      },
    },
    {
      width: '0%',
      render: (row, record, index) => (
        <>
          <HiddenInputs
            names={[
              `menuDocumentFiles[${index}].id`,
              `menuDocumentFiles[${index}].fileName`,
              `menuDocumentFiles[${index}].fileBucketName`,
              `menuDocumentFiles[${index}].fileKey`,
              `menuDocumentFiles[${index}].isSelected`,
              `menuDocumentFiles[${index}].fileInheritedCategory`,
              `menuDocumentFiles[${index}].fileCategory`,
              `menuDocumentFiles[${index}].fileInheritedTitle`,
              `menuDocumentFiles[${index}].filePredictionCategory`,
              `menuDocumentFiles[${index}].filePredictionTitle`,
              `menuDocumentFiles[${index}].filePredictionMenuScore`,
              `menuDocumentFiles[${index}].filePredictionDishCount`,
              `menuDocumentFiles[${index}].fileText`,
              `menuDocumentFiles[${index}].filePredictionRationale`,
            ]}
          />
        </>
      ),
    },
  ]

  return (
    // Must be a Panel child of parent Collapse
    <div>
      <div
        style={{
          overflowX: 'auto',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
        }}
      >
        <Table
          columns={menuDocumentFilesTableColumns}
          dataSource={Array.isArray(menuDocumentFiles) ? menuDocumentFiles : []}
          rowKey={record => record.id}
          pagination={false}
          scroll={{ x: 2500 }}
          style={{ width: '2500px' }}
          tableLayout="fixed"
        />
      </div>
    </div>
  )
}

MenuDocumentFilesSection.propTypes = {
  menuDocumentFiles: PropTypes.array,
  onMenuDocumentFileSelect: PropTypes.func.isRequired,
  onFileCategoryChange: PropTypes.func.isRequired,
  applyInheritedCategoryToConfirmed: PropTypes.func.isRequired,
  applyPredictedCategoryToConfirmed: PropTypes.func.isRequired,
  applyInheritedTitleToConfirmed: PropTypes.func.isRequired,
  applyPredictedTitleToConfirmed: PropTypes.func.isRequired,
  onMenuDocumentFileNameClick: PropTypes.func.isRequired,
}

MenuDocumentFilesSection.defaultProps = {
  menuDocumentFiles: [],
}

export default MenuDocumentFilesSection
